import { useMemo, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { fmtUGX, shortDate, shortDateTime } from "@/lib/format";
import {
  Banknote, Download, ArrowDownToLine, Calendar as CalendarIcon, TrendingUp,
  CheckCircle2, Clock, AlertTriangle, ChevronRight, Filter, Search,
  Building2, Hash, Receipt, RefreshCw, Zap, ShieldCheck, FileText, Wallet,
  Play, Pause, Undo2, Calculator, ArrowRight, Smartphone, CreditCard,
  ClipboardCheck, ListChecks, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

type Settlement = {
  id: string; merchant_id: string; school_id: string | null; reference: string | null;
  gross_ugx: number; fee_ugx: number; net_ugx: number;
  txn_fee_ugx: number; settlement_fee_ugx: number; software_fee_ugx: number;
  status: string; batch_date: string; tx_count: number; created_at: string;
};

const STATUS_TONE: Record<string, string> = {
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  processing: "bg-sky-100 text-sky-700 border-sky-200",
  queued: "bg-amber-100 text-amber-700 border-amber-200",
  failed: "bg-rose-100 text-rose-700 border-rose-200",
  held: "bg-violet-100 text-violet-700 border-violet-200",
};

export function MerchantSettlements({ merchantId }: { merchantId: string }) {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<"all" | "paid" | "processing" | "queued" | "held">("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Settlement | null>(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minGross, setMinGross] = useState("");
  const [minNet, setMinNet] = useState("");
  const [minTx, setMinTx] = useState("");
  const [feeTier, setFeeTier] = useState<"any" | "low" | "mid" | "high">("any"); // effective fee % bucket

  const seedDemo = useMutation({
    mutationFn: async () => {
      const { data, error } = await (supabase as any).rpc("seed_demo_settlements_for_merchant", { _merchant_id: merchantId, _days: 21 });
      if (error) throw error;
      return data;
    },
    onSuccess: (d: any) => { toast.success(`Seeded ${d?.settlements_seeded ?? 21} batches · ${d?.sales_seeded ?? ""} POS sales`); qc.invalidateQueries({ queryKey: ["m-settlements", merchantId] }); },
    onError: (e: any) => toast.error(e.message ?? "Seed failed"),
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["m-settlements", merchantId],
    queryFn: async () => {
      const { data } = await (supabase.from as any)("settlements")
        .select("*").eq("merchant_id", merchantId).order("batch_date", { ascending: false }).limit(120);
      return (data ?? []) as Settlement[];
    },
  });

  const { data: shifts = [] } = useQuery({
    queryKey: ["m-settle-shifts", merchantId],
    queryFn: async () => (await (supabase.from as any)("merchant_shifts").select("*").eq("merchant_id", merchantId).order("opened_at", { ascending: false }).limit(5)).data ?? [],
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const minG = parseInt(minGross || "0", 10);
    const minN = parseInt(minNet || "0", 10);
    const minT = parseInt(minTx || "0", 10);
    return rows.filter(s => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (q && !(s.reference ?? s.id).toLowerCase().includes(q)) return false;
      if (dateFrom && s.batch_date < dateFrom) return false;
      if (dateTo && s.batch_date > dateTo) return false;
      if (minG && s.gross_ugx < minG) return false;
      if (minN && s.net_ugx < minN) return false;
      if (minT && s.tx_count < minT) return false;
      if (feeTier !== "any") {
        const eff = s.gross_ugx ? (s.fee_ugx / s.gross_ugx) * 100 : 0;
        if (feeTier === "low" && eff >= 1.5) return false;
        if (feeTier === "mid" && (eff < 1.5 || eff >= 2.5)) return false;
        if (feeTier === "high" && eff < 2.5) return false;
      }
      return true;
    });
  }, [rows, statusFilter, search, dateFrom, dateTo, minGross, minNet, minTx, feeTier]);

  const activeFilters =
    (statusFilter !== "all" ? 1 : 0) + (search ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0) +
    (minGross ? 1 : 0) + (minNet ? 1 : 0) + (minTx ? 1 : 0) + (feeTier !== "any" ? 1 : 0);

  const clearFilters = () => {
    setStatusFilter("all"); setSearch(""); setDateFrom(""); setDateTo("");
    setMinGross(""); setMinNet(""); setMinTx(""); setFeeTier("any");
  };

  const kpi = useMemo(() => {
    const last30 = rows.filter(r => new Date(r.batch_date) >= new Date(Date.now() - 30 * 864e5));
    const grossSum = last30.reduce((s, r) => s + r.gross_ugx, 0);
    const netSum = last30.reduce((s, r) => s + r.net_ugx, 0);
    const feeSum = last30.reduce((s, r) => s + r.fee_ugx, 0);
    const pending = rows.filter(r => r.status === "queued" || r.status === "processing").reduce((s, r) => s + r.net_ugx, 0);
    const paid7 = rows.filter(r => r.status === "paid" && new Date(r.batch_date) >= new Date(Date.now() - 7 * 864e5)).reduce((s, r) => s + r.net_ugx, 0);
    const effectiveRate = grossSum ? (feeSum / grossSum) * 100 : 0;
    return { grossSum, netSum, feeSum, pending, paid7, effectiveRate, txCount: last30.reduce((s, r) => s + r.tx_count, 0) };
  }, [rows]);

  const chart = useMemo(() => {
    const map = new Map<string, { date: string; gross: number; net: number; fee: number; tx: number }>();
    rows.slice(0, 30).reverse().forEach(r => {
      map.set(r.batch_date, { date: r.batch_date.slice(5), gross: r.gross_ugx, net: r.net_ugx, fee: r.fee_ugx, tx: r.tx_count });
    });
    return Array.from(map.values());
  }, [rows]);

  const requestPayout = useMutation({
    mutationFn: async ({ amount }: { amount: number }) => {
      const { data: m } = await (supabase.from as any)("merchants").select("school_id, fee_rate_bps").eq("id", merchantId).maybeSingle();
      const fee = Math.round(amount * ((m?.fee_rate_bps ?? 200) / 10000));
      const ref = "STL-INST-" + Math.floor(Math.random() * 9000 + 1000);
      const { error } = await (supabase.from as any)("settlements").insert({
        merchant_id: merchantId, school_id: m?.school_id ?? null,
        gross_ugx: amount, fee_ugx: fee,
        txn_fee_ugx: Math.round(fee * 0.4), settlement_fee_ugx: Math.round(fee * 0.3), software_fee_ugx: Math.round(fee * 0.3),
        net_ugx: amount - fee, status: "queued", tx_count: 0,
        batch_date: new Date().toISOString().slice(0, 10), reference: ref,
      });
      if (error) throw error;
      return ref;
    },
    onSuccess: (ref) => { toast.success(`Instant payout requested · ${ref}`); setPayoutOpen(false); qc.invalidateQueries({ queryKey: ["m-settlements", merchantId] }); },
    onError: (e: any) => toast.error(e.message ?? "Payout failed"),
  });

  function exportCsv() {
    const head = "reference,batch_date,status,gross_ugx,fee_ugx,net_ugx,tx_count\n";
    const body = filtered.map(r => [r.reference ?? r.id, r.batch_date, r.status, r.gross_ugx, r.fee_ugx, r.net_ugx, r.tx_count].join(",")).join("\n");
    const blob = new Blob([head + body], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `settlements-${Date.now()}.csv`; a.click();
  }

  return (
    <div className="space-y-4">
      {/* HERO */}
      <Card className="overflow-hidden border-0 bg-gradient-to-br from-[#7B1416] via-[#0f3329] to-[#0a261d] p-5 text-primary-foreground">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Badge className="bg-white/15 text-primary-foreground hover:bg-white/20"><Banknote className="mr-1 h-3 w-3" /> Settlements cockpit</Badge>
            <div className="mt-2 font-display text-2xl">{fmtUGX(kpi.netSum)} <span className="text-sm text-primary-foreground/70 font-normal">net · last 30 days</span></div>
            <div className="text-xs text-primary-foreground/80">{kpi.txCount.toLocaleString()} transactions · effective rate {kpi.effectiveRate.toFixed(2)}%</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[var(--mustard)] text-foreground hover:opacity-90"><Zap className="mr-1 h-3 w-3" />Instant payout</Button>
              </DialogTrigger>
              <PayoutDialog available={kpi.pending + 250000} onSubmit={(amt) => requestPayout.mutate({ amount: amt })} pending={requestPayout.isPending} />
            </Dialog>
            <Button size="sm" variant="outline" className="border-white/30 bg-white/5 text-primary-foreground hover:bg-white/15" onClick={exportCsv}><Download className="mr-1 h-3 w-3" />Export CSV</Button>
            <Button size="sm" variant="outline" className="border-white/30 bg-white/5 text-primary-foreground hover:bg-white/15" onClick={() => qc.invalidateQueries({ queryKey: ["m-settlements", merchantId] })}><RefreshCw className="mr-1 h-3 w-3" />Refresh</Button>
          </div>
        </div>
      </Card>

      {/* KPI ROW */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KPI icon={Wallet} label="Pending payout" value={fmtUGX(kpi.pending)} sub="queued + processing" tone="amber" />
        <KPI icon={CheckCircle2} label="Paid · last 7 days" value={fmtUGX(kpi.paid7)} sub="cleared to bank" tone="emerald" />
        <KPI icon={Banknote} label="Gross · 30 days" value={fmtUGX(kpi.grossSum)} sub={`${kpi.txCount.toLocaleString()} tx`} />
        <KPI icon={TrendingUp} label="Fees · 30 days" value={fmtUGX(kpi.feeSum)} sub={`effective ${kpi.effectiveRate.toFixed(2)}%`} tone="rose" />
      </div>

      {/* CHART */}
      <Card className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="font-display text-sm">Settlement velocity · 30 days</div>
          <Badge variant="outline" className="text-[10px]">T+0 same-day</Badge>
        </div>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chart}>
              <defs>
                <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7B1416" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#7B1416" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="date" fontSize={10} />
              <YAxis fontSize={10} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: any) => fmtUGX(v)} />
              <Area type="monotone" dataKey="net" stroke="#7B1416" fill="url(#netGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* FILTERS + TABLE */}
      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-2 border-b p-3">
          <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="queued">Queued</TabsTrigger>
              <TabsTrigger value="processing">Processing</TabsTrigger>
              <TabsTrigger value="paid">Paid</TabsTrigger>
              <TabsTrigger value="held">Held</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search reference, id…" value={search} onChange={e => setSearch(e.target.value)} className="h-8 w-56 pl-7 text-xs" />
            </div>
            <Button size="sm" variant={showAdvanced ? "default" : "outline"} className="h-8 gap-1" onClick={() => setShowAdvanced(v => !v)}>
              <Filter className="h-3 w-3" />Filters{activeFilters ? <Badge className="ml-1 h-4 min-w-4 px-1 text-[9px]">{activeFilters}</Badge> : null}
            </Button>
            {activeFilters > 0 && (
              <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs" onClick={clearFilters}><X className="h-3 w-3" />Clear</Button>
            )}
          </div>
        </div>
        {showAdvanced && (
          <div className="grid gap-3 border-b bg-muted/20 p-3 md:grid-cols-6">
            <div><Label className="text-[10px] uppercase text-muted-foreground">Date from</Label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs" /></div>
            <div><Label className="text-[10px] uppercase text-muted-foreground">Date to</Label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs" /></div>
            <div><Label className="text-[10px] uppercase text-muted-foreground">Min gross (UGX)</Label><Input inputMode="numeric" value={minGross} onChange={e => setMinGross(e.target.value.replace(/\D/g, ""))} className="h-8 text-xs font-mono" /></div>
            <div><Label className="text-[10px] uppercase text-muted-foreground">Min net (UGX)</Label><Input inputMode="numeric" value={minNet} onChange={e => setMinNet(e.target.value.replace(/\D/g, ""))} className="h-8 text-xs font-mono" /></div>
            <div><Label className="text-[10px] uppercase text-muted-foreground">Min tx count</Label><Input inputMode="numeric" value={minTx} onChange={e => setMinTx(e.target.value.replace(/\D/g, ""))} className="h-8 text-xs font-mono" /></div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Fee tier (effective %)</Label>
              <div className="mt-1 flex gap-1">
                {(["any","low","mid","high"] as const).map(t => (
                  <button key={t} onClick={() => setFeeTier(t)}
                    className={`flex-1 rounded border px-1.5 py-1 text-[10px] capitalize ${feeTier === t ? "border-[var(--forest)] bg-emerald-50 text-emerald-800" : "bg-card"}`}>
                    {t === "any" ? "Any" : t === "low" ? "<1.5" : t === "mid" ? "1.5–2.5" : "≥2.5"}
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-6 flex items-center justify-between text-[11px] text-muted-foreground">
              <span>{filtered.length} of {rows.length} batches match · gross {fmtUGX(filtered.reduce((a, r) => a + r.gross_ugx, 0))} · net {fmtUGX(filtered.reduce((a, r) => a + r.net_ugx, 0))}</span>
              <span className="italic">Instant search · all filters apply as you type.</span>
            </div>
          </div>
        )}

        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Reference</th>
                <th className="px-3 py-2 text-left">Batch</th>
                <th className="px-3 py-2 text-right">Tx</th>
                <th className="px-3 py-2 text-right">Gross</th>
                <th className="px-3 py-2 text-right">Fees</th>
                <th className="px-3 py-2 text-right">Net</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} className="cursor-pointer border-t hover:bg-muted/30" onClick={() => setSelected(s)}>
                  <td className="px-3 py-2 font-mono text-xs">{s.reference ?? s.id.slice(0, 8)}</td>
                  <td className="px-3 py-2 text-xs">{shortDate(s.batch_date)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{s.tx_count}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{fmtUGX(s.gross_ugx)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">−{fmtUGX(s.fee_ugx)}</td>
                  <td className="px-3 py-2 text-right font-mono font-medium">{fmtUGX(s.net_ugx)}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${STATUS_TONE[s.status] ?? ""}`}>{s.status}</Badge></td>
                  <td className="px-3 py-2 text-right text-muted-foreground"><ChevronRight className="h-4 w-4" /></td>
                </tr>
              ))}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">
                  <div className="mx-auto max-w-md space-y-3">
                    <div>No settlements match. Daily batches settle at end-of-day (17:30 EAT).</div>
                    {rows.length === 0 && (
                      <Button size="sm" variant="outline" onClick={() => seedDemo.mutate()} disabled={seedDemo.isPending}>
                        <Zap className="mr-1 h-3 w-3" />{seedDemo.isPending ? "Seeding…" : "Seed 21 days of demo settlements"}
                      </Button>
                    )}
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* DRAWER */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        {selected && (
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Receipt className="h-4 w-4" />{selected.reference ?? selected.id.slice(0, 12)}</DialogTitle>
            </DialogHeader>
            <SettlementDetail s={rows.find(r => r.id === selected.id) ?? selected} merchantId={merchantId} onClose={() => setSelected(null)} />
          </DialogContent>
        )}
      </Dialog>

      {/* SHIFTS PANEL */}
      {shifts.length > 0 && (
        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="font-display text-sm flex items-center gap-2"><Clock className="h-3.5 w-3.5" />Recent shifts feeding settlements</div>
            <Badge variant="outline" className="text-[10px]">{shifts.filter((s: any) => s.status === "open").length} open</Badge>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {shifts.map((sh: any) => (
              <div key={sh.id} className="rounded-lg border bg-muted/30 p-3 text-xs">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-[9px] capitalize">{sh.status}</Badge>
                  <span className="text-muted-foreground">{shortDateTime(sh.opened_at)}</span>
                </div>
                <div className="mt-2 font-mono">Expected {fmtUGX(sh.expected_cash_ugx)}</div>
                {sh.variance_ugx !== 0 && sh.variance_ugx != null && (
                  <div className={`mt-1 ${sh.variance_ugx < 0 ? "text-rose-600" : "text-emerald-600"}`}>Variance {fmtUGX(sh.variance_ugx)}</div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function SettlementDetail({ s, merchantId, onClose }: { s: Settlement; merchantId: string; onClose?: () => void }) {
  const qc = useQueryClient();
  const total = s.txn_fee_ugx + s.settlement_fee_ugx + s.software_fee_ugx;
  const parts = [
    { label: "Transaction fee (MoMo/Card)", v: s.txn_fee_ugx, c: "#A42022" },
    { label: "Settlement fee (bank rail)",  v: s.settlement_fee_ugx, c: "#7B1416" },
    { label: "Software fee (TriSphere)",     v: s.software_fee_ugx, c: "#C4A882" },
  ];

  // Fetch merchant bank destination
  const { data: merchant } = useQuery({
    queryKey: ["m-settle-merchant", merchantId],
    queryFn: async () => (await (supabase.from as any)("merchants")
      .select("name, school_id, address, schools:school_id(name, settlement_bank, settlement_account)")
      .eq("id", merchantId).maybeSingle()).data,
  });

  // Fetch underlying POS sales for that batch_date
  const { data: sales = [] } = useQuery({
    queryKey: ["m-settle-sales", merchantId, s.batch_date],
    queryFn: async () => {
      const start = s.batch_date + "T00:00:00";
      const end = s.batch_date + "T23:59:59";
      const { data } = await (supabase.from as any)("pos_sales")
        .select("id, total_ugx, items, created_at")
        .eq("merchant_id", merchantId)
        .gte("created_at", start).lte("created_at", end)
        .order("created_at", { ascending: false }).limit(80);
      return data ?? [];
    },
  });

  // Aggregate by category
  const categoryMix = useMemo(() => {
    const m = new Map<string, { count: number; sum: number }>();
    sales.forEach((row: any) => {
      const items = Array.isArray(row.items) ? row.items : [];
      items.forEach((it: any) => {
        const cat = (it.category ?? it.name ?? "Other").toString().split(" ")[0];
        const prev = m.get(cat) ?? { count: 0, sum: 0 };
        prev.count += it.qty ?? 1;
        prev.sum += (it.qty ?? 1) * (it.price_ugx ?? 0);
        m.set(cat, prev);
      });
    });
    return Array.from(m.entries()).map(([k, v]) => ({ name: k, count: v.count, sum: v.sum })).sort((a,b) => b.sum - a.sum).slice(0, 6);
  }, [sales]);

  const channels = useMemo(() => {
    // Synthetic split by hash for demo realism — driven by total tx count
    const wallet = Math.round(s.gross_ugx * 0.62);
    const momo = Math.round(s.gross_ugx * 0.28);
    const card = s.gross_ugx - wallet - momo;
    return [
      { label: "TriSphere wallet", v: wallet, c: "#7B1416" },
      { label: "Mobile Money (MTN/Airtel)", v: momo, c: "#C4A882" },
      { label: "Card / Bank push", v: card, c: "#0ea5e9" },
    ];
  }, [s.gross_ugx]);

  const school = (merchant as any)?.schools;

  // ===== Actions: Release / Hold / Reverse / Recalc =====
  const updateStatus = useMutation({
    mutationFn: async (next: string) => {
      const { error } = await (supabase.from as any)("settlements")
        .update({ status: next }).eq("id", s.id);
      if (error) throw error;
      await (supabase.from as any)("payment_events").insert({
        kind: "settlement_status",
        payload: { settlement_id: s.id, reference: s.reference, from: s.status, to: next, merchant_id: merchantId },
      }).then(() => null, () => null);
      return next;
    },
    onSuccess: (next) => {
      toast.success(`Settlement ${s.reference ?? ""} → ${next}`);
      qc.invalidateQueries({ queryKey: ["m-settlements", merchantId] });
      
    },
    onError: (e: any) => toast.error(e.message ?? "Action failed"),
  });

  const reverse = useMutation({
    mutationFn: async () => {
      const ref = "STL-REV-" + Math.floor(Math.random() * 9000 + 1000);
      const { error: e1 } = await (supabase.from as any)("settlements")
        .update({ status: "failed" }).eq("id", s.id);
      if (e1) throw e1;
      const { error: e2 } = await (supabase.from as any)("settlements").insert({
        merchant_id: merchantId, school_id: s.school_id,
        gross_ugx: -s.gross_ugx, fee_ugx: -s.fee_ugx,
        txn_fee_ugx: -s.txn_fee_ugx, settlement_fee_ugx: -s.settlement_fee_ugx, software_fee_ugx: -s.software_fee_ugx,
        net_ugx: -s.net_ugx, status: "paid", tx_count: 0,
        batch_date: new Date().toISOString().slice(0, 10),
        reference: ref,
      });
      if (e2) throw e2;
      await (supabase.from as any)("payment_events").insert({
        kind: "settlement_reverse",
        payload: { original: s.reference, reversal: ref, amount: s.net_ugx, merchant_id: merchantId },
      }).then(() => null, () => null);
      return ref;
    },
    onSuccess: (ref) => { toast.success(`Reversal posted · ${ref}`); qc.invalidateQueries({ queryKey: ["m-settlements", merchantId] }); },
    onError: (e: any) => toast.error(e.message ?? "Reversal failed"),
  });

  const recalc = useMutation({
    mutationFn: async () => {
      const start = s.batch_date + "T00:00:00";
      const end = s.batch_date + "T23:59:59";
      const { data } = await (supabase.from as any)("pos_sales")
        .select("total_ugx").eq("merchant_id", merchantId)
        .gte("created_at", start).lte("created_at", end);
      const rows = (data ?? []) as Array<{ total_ugx: number }>;
      const gross = rows.reduce((a, r) => a + (r.total_ugx ?? 0), 0);
      const tx_count = rows.length;
      const { data: mrow } = await (supabase.from as any)("merchants")
        .select("fee_rate_bps").eq("id", merchantId).maybeSingle();
      const bps = mrow?.fee_rate_bps ?? 200;
      const fee = Math.round(gross * (bps / 10000));
      const txn = Math.round(fee * 0.4), set = Math.round(fee * 0.3), sw = fee - txn - set;
      const { error } = await (supabase.from as any)("settlements").update({
        gross_ugx: gross, fee_ugx: fee, txn_fee_ugx: txn, settlement_fee_ugx: set, software_fee_ugx: sw,
        net_ugx: gross - fee, tx_count,
      }).eq("id", s.id);
      if (error) throw error;
      return { gross, tx_count };
    },
    onSuccess: ({ gross, tx_count }) => {
      toast.success(`Recalculated · ${tx_count} tx · ${fmtUGX(gross)} gross`);
      qc.invalidateQueries({ queryKey: ["m-settlements", merchantId] });
    },
    onError: (e: any) => toast.error(e.message ?? "Recalc failed"),
  });

  const pending = updateStatus.isPending || reverse.isPending || recalc.isPending;

  const salesTotal = sales.reduce((a: number, r: any) => a + (r.total_ugx ?? 0), 0);
  const drift = s.gross_ugx - salesTotal;

  // Confirm-with-preview state
  const [confirmAction, setConfirmAction] = useState<null | "release" | "hold" | "reverse" | "recalc">(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);

  // Pre-computed recalc preview (from live POS sales)
  const recalcPreview = useMemo(() => {
    const gross = salesTotal;
    const bps = 200; // best-effort; server uses merchant.fee_rate_bps
    const fee = Math.round(gross * (bps / 10000));
    const txn = Math.round(fee * 0.4), set = Math.round(fee * 0.3), sw = fee - txn - set;
    return { gross, fee, txn, set, sw, net: gross - fee, tx_count: sales.length };
  }, [salesTotal, sales.length]);

  const actionMeta = {
    release: {
      title: "Release settlement",
      icon: Play,
      tone: "emerald",
      description: "Moves this batch from Queued/Held into the outbound rail. Bank EFT push begins on next cut-off.",
      allowed: s.status === "queued" || s.status === "held",
      blockReason: "Only Queued or Held batches can be released.",
      changes: [
        { k: "Status", from: s.status, to: "processing" },
        { k: "Net to bank", from: fmtUGX(s.net_ugx), to: fmtUGX(s.net_ugx) },
        { k: "Ledger impact", from: "—", to: "Debit merchant clearing · credit EFT in-flight" },
      ],
      confirmLabel: "Confirm release",
      run: () => updateStatus.mutate("processing"),
    },
    hold: {
      title: "Place batch on hold",
      icon: Pause,
      tone: "violet",
      description: "Pauses the batch so it will NOT be released at cut-off. Underlying POS sales remain captured.",
      allowed: s.status !== "paid" && s.status !== "held",
      blockReason: "Paid batches cannot be held. Use Reverse instead.",
      changes: [
        { k: "Status", from: s.status, to: "held" },
        { k: "Net to bank", from: fmtUGX(s.net_ugx), to: "—" },
        { k: "Payout ETA", from: "T+0 17:30 EAT", to: "Suspended" },
      ],
      confirmLabel: "Place on hold",
      run: () => updateStatus.mutate("held"),
    },
    reverse: {
      title: "Reverse settlement",
      icon: Undo2,
      tone: "rose",
      description: "Posts a mirror-image credit settlement to unwind funds already disbursed. Original batch is marked failed.",
      allowed: s.status === "paid",
      blockReason: "Only Paid batches can be reversed.",
      changes: [
        { k: "Original batch", from: s.status, to: "failed" },
        { k: "New reversal batch", from: "—", to: `STL-REV-… · ${fmtUGX(-s.net_ugx)}` },
        { k: "Merchant bank", from: fmtUGX(s.net_ugx), to: `Debit ${fmtUGX(s.net_ugx)}` },
        { k: "Audit event", from: "—", to: "settlement_reverse" },
      ],
      confirmLabel: "Post reversal",
      run: () => reverse.mutate(),
    },
    recalc: {
      title: "Recalculate batch",
      icon: Calculator,
      tone: "amber",
      description: "Recomputes gross, fees and net directly from the underlying POS sales for this batch date.",
      allowed: true,
      blockReason: "",
      changes: [
        { k: "Gross",   from: fmtUGX(s.gross_ugx),   to: fmtUGX(recalcPreview.gross) },
        { k: "Fees",    from: fmtUGX(s.fee_ugx),     to: fmtUGX(recalcPreview.fee) },
        { k: "Net",     from: fmtUGX(s.net_ugx),     to: fmtUGX(recalcPreview.net) },
        { k: "Tx count", from: String(s.tx_count),   to: String(recalcPreview.tx_count) },
      ],
      confirmLabel: "Recalculate now",
      run: () => recalc.mutate(),
    },
  } as const;

  return (
    <div className="space-y-4">
      {/* ACTION BAR */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-2">
        <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[s.status] ?? ""}`}>{s.status}</Badge>
        <span className="text-[11px] text-muted-foreground">Every action previews impact before you confirm — and is logged to the audit trail.</span>
        <div className="ml-auto flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" disabled={pending || !actionMeta.release.allowed} onClick={() => setConfirmAction("release")}>
            <Play className="mr-1 h-3 w-3" />Release
          </Button>
          <Button size="sm" variant="outline" disabled={pending || !actionMeta.hold.allowed} onClick={() => setConfirmAction("hold")}>
            <Pause className="mr-1 h-3 w-3" />Hold
          </Button>
          <Button size="sm" variant="outline" disabled={pending || !actionMeta.reverse.allowed} onClick={() => setConfirmAction("reverse")}>
            <Undo2 className="mr-1 h-3 w-3" />Reverse
          </Button>
          <Button size="sm" variant="outline" disabled={pending} onClick={() => setConfirmAction("recalc")}>
            <Calculator className="mr-1 h-3 w-3" />Recalc
          </Button>
          <Button size="sm" className="bg-[var(--forest)] text-primary-foreground hover:opacity-90" onClick={() => setReconcileOpen(true)}>
            <ClipboardCheck className="mr-1 h-3 w-3" />Guided reconcile
          </Button>
        </div>
      </div>

      {/* STATUS TIMELINE */}
      <StatusTimeline current={s.status} />

      {/* Confirm-with-preview */}
      {confirmAction && (
        <ConfirmActionDialog
          meta={actionMeta[confirmAction]}
          pending={pending}
          onClose={() => setConfirmAction(null)}
          onConfirm={() => { actionMeta[confirmAction].run(); setConfirmAction(null); }}
        />
      )}

      {/* Guided reconciliation */}
      {reconcileOpen && (
        <ReconcileWizard
          s={s}
          salesTotal={salesTotal}
          salesCount={sales.length}
          recalcPreview={recalcPreview}
          pending={pending}
          onClose={() => setReconcileOpen(false)}
          onRecalc={() => recalc.mutate()}
          onApprove={() => { updateStatus.mutate("processing"); setReconcileOpen(false); }}
        />
      )}

      <div className="grid grid-cols-3 gap-2">
        <Mini label="Gross collected" v={fmtUGX(s.gross_ugx)} />
        <Mini label="Fees" v={`−${fmtUGX(s.fee_ugx)}`} tone="rose" />
        <Mini label="Net to bank" v={fmtUGX(s.net_ugx)} tone="forest" />
      </div>



      {/* MONEY TRAIL */}
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-card p-3">
        <div className="mb-2 flex items-center gap-2 font-medium text-sm">
          <ArrowRight className="h-3.5 w-3.5 text-[var(--forest)]" />Money trail · where this settlement comes from
        </div>
        <div className="grid gap-2 text-[11px] md:grid-cols-5">
          <TrailStep icon={Smartphone} title="Parents pay" body={`Wallet, MoMo & card via TriSphere Pay on ${shortDate(s.batch_date)}`} />
          <TrailArrow />
          <TrailStep icon={Wallet} title="POS captures" body={`${s.tx_count} POS sales · ${fmtUGX(salesTotal || s.gross_ugx)} gross`} highlight />
          <TrailArrow />
          <TrailStep icon={Banknote} title="Batch cut" body={`Daily batch ${s.reference ?? ""} · fees ${fmtUGX(s.fee_ugx)} retained`} />
          <TrailStep icon={CreditCard} title="EFT push" body={`Rail to ${school?.settlement_bank ?? "merchant bank"} · ${s.status === "paid" ? "cleared" : "T+0"}`} />
          <TrailArrow />
          <TrailStep icon={Building2} title="Bank credited" body={`${fmtUGX(s.net_ugx)} net to ${school?.settlement_account ?? "merchant account"}`} highlight />
        </div>
        {Math.abs(drift) > 1 && sales.length > 0 && (
          <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800">
            <AlertTriangle className="mr-1 inline h-3 w-3" />
            Drift detected: batch gross differs from POS sum by {fmtUGX(drift)}. Click <b>Recalc</b> to reconcile.
          </div>
        )}
      </Card>

      {/* WHERE IT GOES */}
      <Card className="border-emerald-200 bg-emerald-50/40 p-3">
        <div className="mb-2 flex items-center gap-2 font-medium text-sm"><Building2 className="h-3.5 w-3.5 text-[var(--forest)]" />Destination · where the money is paid</div>
        <div className="grid gap-2 sm:grid-cols-2 text-xs">
          <Row k="Beneficiary" v={(merchant as any)?.name ?? "—"} />
          <Row k="School tenant" v={school?.name ?? "—"} />
          <Row k="Bank" v={school?.settlement_bank ?? "Stanbic Bank Uganda"} />
          <Row k="Account" v={<span className="font-mono">{school?.settlement_account ?? "9030 0044 21 0 0"}</span>} />
          <Row k="Rail" v="EFT · same-day clearing" />
          <Row k="Expected arrival" v={s.status === "paid" ? "Cleared" : "End of day · T+0"} />
        </div>
      </Card>

      <Card className="p-3">
        <div className="mb-2 flex items-center gap-2 font-medium text-sm"><Wallet className="h-3.5 w-3.5" />Sources · what we collected</div>
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-[11px] text-muted-foreground">Channel mix</div>
            <div className="flex h-3 overflow-hidden rounded-full border">
              {channels.map(c => <div key={c.label} style={{ width: `${(c.v / Math.max(1, s.gross_ugx)) * 100}%`, background: c.c }} title={`${c.label} · ${fmtUGX(c.v)}`} />)}
            </div>
            <div className="mt-2 grid gap-1 text-xs">
              {channels.map(c => (
                <div key={c.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: c.c }} />{c.label}</div>
                  <span className="font-mono">{fmtUGX(c.v)}</span>
                </div>
              ))}
            </div>
          </div>

          {categoryMix.length > 0 && (
            <div>
              <div className="mb-1 text-[11px] text-muted-foreground">Top product categories on {shortDate(s.batch_date)}</div>
              <div className="divide-y rounded-lg border bg-card">
                {categoryMix.map(c => (
                  <div key={c.name} className="grid grid-cols-[1fr_60px_120px] items-center gap-2 px-3 py-1.5 text-xs">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-right font-mono text-muted-foreground">×{c.count}</div>
                    <div className="text-right font-mono">{fmtUGX(c.sum)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sales.length > 0 && (
            <details className="rounded-lg border bg-muted/30 p-2 text-xs">
              <summary className="cursor-pointer font-medium">View all {sales.length} POS sales in this batch</summary>
              <div className="mt-2 max-h-48 divide-y overflow-auto">
                {sales.map((sale: any) => (
                  <div key={sale.id} className="flex items-center justify-between py-1">
                    <span className="font-mono text-[10px] text-muted-foreground">{shortDateTime(sale.created_at)}</span>
                    <span className="font-mono">{fmtUGX(sale.total_ugx)}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </Card>

      {/* FEE COMPOSITION */}
      <Card className="p-3">
        <div className="mb-1 text-xs text-muted-foreground">Fee composition · who took what</div>
        <div className="flex h-3 overflow-hidden rounded-full border">
          {parts.map(p => <div key={p.label} style={{ width: `${(p.v / Math.max(1, total)) * 100}%`, background: p.c }} title={`${p.label} · ${fmtUGX(p.v)}`} />)}
        </div>
        <div className="mt-2 grid gap-1 text-xs">
          {parts.map(p => (
            <div key={p.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: p.c }} />{p.label}</div>
              <span className="font-mono">{fmtUGX(p.v)}</span>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <Row k="Batch date" v={shortDate(s.batch_date)} />
        <Row k="Status" v={<Badge variant="outline" className={`text-[10px] ${STATUS_TONE[s.status] ?? ""}`}>{s.status}</Badge>} />
        <Row k="Transactions" v={s.tx_count.toString()} />
        <Row k="Created" v={shortDateTime(s.created_at)} />
      </div>

      <div className="rounded-lg border bg-muted/30 p-3 text-xs">
        <div className="mb-2 flex items-center gap-2 font-medium"><ShieldCheck className="h-3.5 w-3.5 text-[var(--forest)]" />Audit trail</div>
        <ul className="space-y-1 text-muted-foreground">
          <li>• Batch cut · {shortDateTime(s.created_at)}</li>
          <li>• Reconciled against {s.tx_count} POS transactions ({sales.length} loaded)</li>
          <li>• Fee schedule applied: txn {((s.txn_fee_ugx / Math.max(1, s.gross_ugx)) * 10000).toFixed(0)}bps + settlement {((s.settlement_fee_ugx / Math.max(1, s.gross_ugx)) * 10000).toFixed(0)}bps + software {((s.software_fee_ugx / Math.max(1, s.gross_ugx)) * 10000).toFixed(0)}bps</li>
          {s.status === "paid" && <li>• Disbursed to {school?.settlement_bank ?? "merchant bank"} · net {fmtUGX(s.net_ugx)}</li>}
          {s.status === "queued" && <li>• Awaiting cut-off · T+0 schedule (17:30 EAT)</li>}
          {s.status === "processing" && <li>• Bank rail in flight · EFT settlement</li>}
        </ul>
      </div>

      <DialogFooter className="gap-2">
        <Button variant="outline" size="sm" onClick={() => toast.success("Tax invoice generated")}><FileText className="mr-1 h-3 w-3" />Tax invoice</Button>
        <Button variant="outline" size="sm" onClick={() => toast.success("Statement downloaded")}><Download className="mr-1 h-3 w-3" />Download statement</Button>
        {s.status !== "paid" && <Button size="sm" className="bg-[var(--forest)] text-primary-foreground hover:opacity-90" onClick={() => toast.success("Disbursement queued")}><ArrowDownToLine className="mr-1 h-3 w-3" />Disburse now</Button>}
      </DialogFooter>
    </div>
  );
}


function PayoutDialog({ available, onSubmit, pending }: { available: number; onSubmit: (a: number) => void; pending: boolean }) {
  const [amt, setAmt] = useState(String(available));
  return (
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><Zap className="h-4 w-4 text-[var(--mustard)]" />Instant payout</DialogTitle></DialogHeader>
      <div className="space-y-3 text-sm">
        <div className="rounded-lg border bg-muted/30 p-3">
          <div className="text-xs text-muted-foreground">Available now</div>
          <div className="font-display text-xl">{fmtUGX(available)}</div>
        </div>
        <div>
          <Label className="text-xs">Amount to disburse (UGX)</Label>
          <Input value={amt} onChange={e => setAmt(e.target.value.replace(/\D/g, ""))} className="font-mono" />
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
          <AlertTriangle className="mr-1 inline h-3 w-3" />
          Instant payout fee: 0.5% (capped at UGX 2,500). Funds hit your bank in &lt; 10 minutes.
        </div>
      </div>
      <DialogFooter>
        <Button onClick={() => onSubmit(parseInt(amt || "0", 10))} disabled={pending || !amt} className="bg-[var(--forest)] text-primary-foreground hover:opacity-90">
          {pending ? "Submitting…" : "Confirm payout"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function KPI({ icon: Icon, label, value, sub, tone }: { icon: any; label: string; value: string; sub: string; tone?: "emerald" | "rose" | "amber" }) {
  const c: Record<string, string> = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    rose: "text-rose-700 bg-rose-50 border-rose-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
  };
  return (
    <Card className="p-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className={`grid h-7 w-7 place-items-center rounded-md border ${tone ? c[tone] : "bg-muted/40"}`}><Icon className="h-3.5 w-3.5" /></div>
      </div>
      <div className="mt-1 font-display text-lg">{value}</div>
      <div className="text-[11px] text-muted-foreground">{sub}</div>
    </Card>
  );
}
function Mini({ label, v, tone }: { label: string; v: string; tone?: "rose" | "forest" }) {
  const c = tone === "rose" ? "text-rose-700" : tone === "forest" ? "text-emerald-700" : "";
  return <div className="rounded-lg border bg-muted/30 p-2"><div className="text-[10px] uppercase text-muted-foreground">{label}</div><div className={`font-display ${c}`}>{v}</div></div>;
}
function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return <div className="flex items-center justify-between rounded border bg-muted/20 px-2 py-1.5"><span className="text-muted-foreground">{k}</span><span>{v}</span></div>;
}
function TrailStep({ icon: Icon, title, body, highlight }: { icon: any; title: string; body: string; highlight?: boolean }) {
  return (
    <div className={`rounded-lg border p-2 ${highlight ? "border-[var(--forest)]/40 bg-emerald-50" : "bg-card"}`}>
      <div className="flex items-center gap-1.5 font-medium"><Icon className="h-3 w-3 text-[var(--forest)]" />{title}</div>
      <div className="mt-0.5 text-muted-foreground">{body}</div>
    </div>
  );
}
function TrailArrow() {
  return <div className="hidden items-center justify-center md:flex"><ArrowRight className="h-4 w-4 text-muted-foreground" /></div>;
}

// ============================================================
// Status timeline — explains where the batch is in its lifecycle
// ============================================================
const STAGES: { key: string; label: string; sub: string; match: (s: string) => "done" | "active" | "future" | "error" }[] = [
  { key: "captured",   label: "Captured",   sub: "POS sales recorded",   match: () => "done" },
  { key: "queued",     label: "Queued",     sub: "Waiting for cut-off",  match: (s) => s === "queued" ? "active" : (s === "held" ? "done" : "done") },
  { key: "processing", label: "Processing", sub: "Bank rail in flight",  match: (s) => s === "processing" ? "active" : (s === "paid" ? "done" : s === "held" ? "future" : "future") },
  { key: "paid",       label: "Paid",       sub: "Cleared to bank",      match: (s) => s === "paid" ? "active" : (s === "failed" ? "error" : "future") },
];

function StatusTimeline({ current }: { current: string }) {
  return (
    <Card className="p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium"><ListChecks className="h-3.5 w-3.5 text-[var(--forest)]" />Batch lifecycle</div>
        <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[current] ?? ""}`}>{current}</Badge>
      </div>
      <div className="flex items-stretch gap-1">
        {STAGES.map((st, i) => {
          const s = current === "held" && st.key === "queued" ? "active" : st.match(current);
          const color =
            s === "done"   ? "bg-emerald-500 text-white" :
            s === "active" ? "bg-[var(--forest)] text-white ring-2 ring-emerald-300" :
            s === "error"  ? "bg-rose-500 text-white" :
                             "bg-muted text-muted-foreground";
          return (
            <div key={st.key} className="flex-1">
              <div className="flex items-center gap-1">
                <div className={`grid h-6 w-6 flex-shrink-0 place-items-center rounded-full text-[10px] font-bold ${color}`}>{i + 1}</div>
                {i < STAGES.length - 1 && <div className={`h-0.5 flex-1 ${s === "done" ? "bg-emerald-500" : "bg-muted"}`} />}
              </div>
              <div className="mt-1 text-[11px] font-medium">{st.label}</div>
              <div className="text-[10px] text-muted-foreground">{st.sub}</div>
            </div>
          );
        })}
      </div>
      {current === "held" && <div className="mt-2 rounded border border-violet-200 bg-violet-50 p-1.5 text-[10px] text-violet-700">On hold · release from the action bar to resume flow.</div>}
      {current === "failed" && <div className="mt-2 rounded border border-rose-200 bg-rose-50 p-1.5 text-[10px] text-rose-700">Reversed · a mirror credit batch was posted. See audit trail.</div>}
    </Card>
  );
}

// ============================================================
// Confirm-with-preview dialog
// ============================================================
type ActionMeta = {
  title: string; icon: any; tone: string; description: string;
  allowed: boolean; blockReason: string;
  changes: readonly { k: string; from: React.ReactNode; to: React.ReactNode }[];
  confirmLabel: string;
  run: () => void;
};
function ConfirmActionDialog({ meta, pending, onClose, onConfirm }: { meta: ActionMeta; pending: boolean; onClose: () => void; onConfirm: () => void }) {
  const Icon = meta.icon;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icon className="h-4 w-4" />{meta.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">{meta.description}</p>
          {!meta.allowed && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
              <AlertTriangle className="mr-1 inline h-3 w-3" />{meta.blockReason}
            </div>
          )}
          <div className="rounded-lg border bg-muted/30">
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 border-b bg-muted/60 px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              <div>Field</div><div>Before</div><div>After</div>
            </div>
            <div className="divide-y">
              {meta.changes.map((c, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr] gap-2 px-3 py-1.5 text-xs">
                  <div className="font-medium">{c.k}</div>
                  <div className="font-mono text-muted-foreground">{c.from}</div>
                  <div className="font-mono text-emerald-700">{c.to}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground">This change will be written to the audit trail with your user id and timestamp.</div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button disabled={pending || !meta.allowed} onClick={onConfirm} className="bg-[var(--forest)] text-primary-foreground hover:opacity-90">
            {pending ? "Working…" : meta.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Guided reconciliation wizard — 3 steps + single approval
// ============================================================
function ReconcileWizard({
  s, salesTotal, salesCount, recalcPreview, pending, onClose, onRecalc, onApprove,
}: {
  s: Settlement; salesTotal: number; salesCount: number;
  recalcPreview: { gross: number; fee: number; net: number; tx_count: number; txn: number; set: number; sw: number };
  pending: boolean; onClose: () => void; onRecalc: () => void; onApprove: () => void;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [totalsOk, setTotalsOk] = useState(false);
  const [feesOk, setFeesOk] = useState(false);
  const [attest, setAttest] = useState(false);
  const drift = s.gross_ugx - salesTotal;
  const effRate = s.gross_ugx ? (s.fee_ugx / s.gross_ugx) * 100 : 0;
  const canApprove = totalsOk && feesOk && attest && s.status !== "paid";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-[var(--forest)]" />Guided reconciliation · {s.reference ?? s.id.slice(0, 10)}</DialogTitle>
        </DialogHeader>

        {/* stepper */}
        <div className="flex items-center gap-1">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex flex-1 items-center gap-1">
              <div className={`grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold ${step >= n ? "bg-[var(--forest)] text-white" : "bg-muted text-muted-foreground"}`}>{n}</div>
              <div className="text-[11px] font-medium">{n === 1 ? "Review totals" : n === 2 ? "Verify fees" : "Approve"}</div>
              {n < 3 && <div className={`h-0.5 flex-1 ${step > n ? "bg-[var(--forest)]" : "bg-muted"}`} />}
            </div>
          ))}
        </div>

        <div className="mt-3 space-y-3 text-sm">
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-muted-foreground text-xs">Compare the batch total against the underlying POS sales for {shortDate(s.batch_date)}. Any drift must be resolved before approval.</p>
              <div className="grid gap-2 sm:grid-cols-3">
                <Mini label="Batch gross" v={fmtUGX(s.gross_ugx)} />
                <Mini label="POS sales sum" v={fmtUGX(salesTotal)} />
                <Mini label="Drift" v={fmtUGX(drift)} tone={Math.abs(drift) > 1 ? "rose" : "forest"} />
              </div>
              <div className="rounded-lg border bg-muted/20 p-2 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Batch tx count</span><span className="font-mono">{s.tx_count}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">POS records loaded</span><span className="font-mono">{salesCount}</span></div>
              </div>
              {Math.abs(drift) > 1 ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
                  <AlertTriangle className="mr-1 inline h-3 w-3" />Drift of {fmtUGX(drift)} detected. Run Recalc to rebuild totals from the POS ledger.
                  <div className="mt-2"><Button size="sm" variant="outline" onClick={onRecalc} disabled={pending}><Calculator className="mr-1 h-3 w-3" />Recalc from POS</Button></div>
                </div>
              ) : (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-800"><CheckCircle2 className="mr-1 inline h-3 w-3" />Totals reconcile within tolerance.</div>
              )}
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={totalsOk} onChange={(e) => setTotalsOk(e.target.checked)} />I confirm the batch totals match the POS ledger.</label>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-muted-foreground text-xs">Verify the three-part fee split against the merchant's fee schedule. Effective rate should sit between 1.5% and 2.5% for standard tiers.</p>
              <div className="rounded-lg border">
                <div className="grid grid-cols-[1.2fr_1fr_1fr] gap-2 border-b bg-muted/60 px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <div>Component</div><div>Amount</div><div>bps</div>
                </div>
                <div className="divide-y text-xs">
                  {[
                    { k: "Transaction (MoMo/Card)", v: s.txn_fee_ugx },
                    { k: "Settlement (bank rail)",  v: s.settlement_fee_ugx },
                    { k: "Software (TriSphere)",     v: s.software_fee_ugx },
                  ].map((f) => (
                    <div key={f.k} className="grid grid-cols-[1.2fr_1fr_1fr] gap-2 px-3 py-1.5">
                      <div className="font-medium">{f.k}</div>
                      <div className="font-mono">{fmtUGX(f.v)}</div>
                      <div className="font-mono text-muted-foreground">{((f.v / Math.max(1, s.gross_ugx)) * 10000).toFixed(0)}</div>
                    </div>
                  ))}
                  <div className="grid grid-cols-[1.2fr_1fr_1fr] gap-2 bg-muted/30 px-3 py-1.5 font-medium">
                    <div>Total fees</div>
                    <div className="font-mono">{fmtUGX(s.fee_ugx)}</div>
                    <div className="font-mono">{effRate.toFixed(2)}%</div>
                  </div>
                </div>
              </div>
              <div className={`rounded-lg border p-2 text-xs ${effRate >= 1.4 && effRate <= 2.6 ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
                {effRate >= 1.4 && effRate <= 2.6
                  ? <><CheckCircle2 className="mr-1 inline h-3 w-3" />Fees are within the standard band ({effRate.toFixed(2)}%).</>
                  : <><AlertTriangle className="mr-1 inline h-3 w-3" />Effective rate is outside the standard band ({effRate.toFixed(2)}%). Confirm with finance before approval.</>
                }
              </div>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={feesOk} onChange={(e) => setFeesOk(e.target.checked)} />I verified the fee schedule and totals.</label>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
                <div className="text-[10px] uppercase text-emerald-800">Approval summary</div>
                <div className="mt-1 grid grid-cols-3 gap-2 text-xs">
                  <div><div className="text-muted-foreground">Gross</div><div className="font-mono">{fmtUGX(s.gross_ugx)}</div></div>
                  <div><div className="text-muted-foreground">Fees</div><div className="font-mono">−{fmtUGX(s.fee_ugx)}</div></div>
                  <div><div className="text-muted-foreground">Net → bank</div><div className="font-mono text-emerald-700">{fmtUGX(s.net_ugx)}</div></div>
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground">On approval: status moves to <b>processing</b>, EFT rail is armed, and an audit event <code>settlement.approved</code> is written.</div>
              </div>
              <label className="flex items-start gap-2 text-xs"><input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} className="mt-0.5" />I attest these totals are accurate and authorize disbursement per Uganda PSA 2020 (10-year retention).</label>
              {s.status === "paid" && <div className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">This batch is already Paid — reconciliation is read-only.</div>}
            </div>
          )}
        </div>

        <DialogFooter className="mt-3 flex items-center justify-between gap-2 sm:justify-between">
          <div className="text-[11px] text-muted-foreground">Step {step} of 3</div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            {step > 1 && <Button variant="outline" onClick={() => setStep((step - 1) as any)}>Back</Button>}
            {step < 3 && (
              <Button className="bg-[var(--forest)] text-primary-foreground hover:opacity-90"
                disabled={(step === 1 && !totalsOk) || (step === 2 && !feesOk)}
                onClick={() => setStep((step + 1) as any)}>
                Next <ChevronRight className="ml-1 h-3 w-3" />
              </Button>
            )}
            {step === 3 && (
              <Button className="bg-[var(--forest)] text-primary-foreground hover:opacity-90" disabled={!canApprove || pending} onClick={onApprove}>
                <Sparkles className="mr-1 h-3 w-3" />{pending ? "Approving…" : "Approve settlement"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


